/**
 * PackedDepSet — Uint32Array-backed set of effect IDs.
 *
 * Drop-in replacement for `Set<ReactiveEffect>` in dependency tracking, with
 * three measurable wins:
 *   - **Memory**: 4 bytes per entry (Uint32) vs ~24-40 bytes for a Set entry
 *     with hash slot + pointer + V8 internal accounting.
 *   - **Cache locality**: linear scans walk a contiguous buffer that fits in
 *     a single L1 cache line for typical signal arity (1-4 subscribers).
 *   - **Pre-sized**: starts with capacity 4, doubles on overflow. Most signals
 *     fit forever in the initial allocation.
 *
 * Layout: `[id0, id1, ..., id(size-1), 0, 0, ...]`. The 0 sentinel is safe
 * because the effect-ID allocator never returns 0 (reserved as "no effect").
 *
 * Tracks size separately from buffer length so deletions can swap-with-last
 * in O(1) without leaving holes.
 */
export class PackedDepSet {
    private _buf: Uint32Array
    private _size: number = 0

    constructor(initialCap = 4) {
        this._buf = new Uint32Array(Math.max(4, initialCap))
    }

    get size(): number {
        return this._size
    }

    get capacity(): number {
        return this._buf.length
    }

    has(id: number): boolean {
        const buf = this._buf
        const len = this._size
        for (let i = 0; i < len; i++) {
            if (buf[i] === id) return true
        }
        return false
    }

    add(id: number): void {
        if (this.has(id)) return
        if (this._size >= this._buf.length) this._grow()
        this._buf[this._size++] = id
    }

    delete(id: number): void {
        const buf = this._buf
        const len = this._size
        for (let i = 0; i < len; i++) {
            if (buf[i] === id) {
                // Swap with last element and shrink — preserves dense packing
                buf[i] = buf[len - 1]
                buf[len - 1] = 0
                this._size--
                return
            }
        }
    }

    forEach(cb: (id: number) => void): void {
        const buf = this._buf
        const len = this._size
        for (let i = 0; i < len; i++) {
            cb(buf[i])
        }
    }

    clear(): void {
        this._buf.fill(0)
        this._size = 0
    }

    toArray(): number[] {
        return Array.from(this._buf.subarray(0, this._size))
    }

    private _grow(): void {
        const newBuf = new Uint32Array(this._buf.length * 2)
        newBuf.set(this._buf)
        this._buf = newBuf
    }
}
